// session/types.ts — Type aliases and default state for the app session

import type {
    SessionState,
    NotificationPreferences,
} from '@shared/types/domain-types'
import { getCurrentSeasonId } from '@/lib/config/governance'

export type BossPhase = 'registration' | 'pre-battle' | 'battle-active' | 'victory' | 'defeat' | 'interval'
export type PetStage = 'EGG' | 'CUB' | 'YOUNG' | 'ADULT'

export type AppSessionState = SessionState
export type AppNotificationType = string
export type AppNotificationPreferences = NotificationPreferences

export const DEFAULT_STATE: SessionState = {
    isAuthenticated: false,
    isLoading: true,
    hydrated: false,
    user: null,
    activeUserId: null,
    userProfile: null,
    firstAccess: false,
    onboardingCompleted: false,
    enabledModules: { core: true, gamificacao: true, tarefas: false, arena: false },
    postOnboardingRedirect: null,
    network: { forceOffline: false, syncQueue: [] },
    accounts: [],
    socialProfiles: [],
    telemetry: { events: [] },
    accessibility: {
        reduceMotion: false,
        highContrast: false,
        fontScale: 'normal',
    },
    arsenal: {
        equippedTitleAchievementId: '',
        featuredAchievementBadgeIds: [],
        activeTheme: 'Core Protocol',
        activeThemeId: 'core-protocol',
        hud: { showPetWidget: true, showBossWidget: true, showKpiWidget: true, expandedXpBar: false },
    },
    cycle: {
        lastSeenYear: new Date().getFullYear(),
        pendingYearReset: null,
        lastSeenSeasonId: getCurrentSeasonId(),
        pendingSeasonResetId: null,
    },
    offlineQueue: {
        items: [],
        syncing: false,
        lastSyncAt: undefined,
    },
    idempotency: {
        processedKeys: [],
    },
    missionClaims: {
        dailyAuto: [],
        weeklyManual: [],
        monthlyManual: [],
    },
    pass: {
        level: 1,
        xp: 0,
        xpPerLevel: 500,
        seasonName: 'Inaugural',
        seasonDaysRemaining: 90,
        claimedRewardIds: [],
    },
    pet: {
        status: 'idle',
        stage: 'CUB',
        attackBonusPercent: 0,
        weekStage: 1,
        lineage: 'common',
    },
    boss: {
        id: null,
        name: '',
        phase: 'interval',
        day: 0,
        totalDays: 0,
        currentHp: 0,
        maxHp: 0,
        participants: [],
        contributions: [],
        dailyAppliedByUser: {},
    },

    inventory: {
        shields: 0,
        revivePotions: 0,
        commonRations: 0,
        fireFruits: 0,
        iceFruits: 0,
        petItems: [],
        cosmetics: [],
        tickets: [],
    },
    habits: [],
    habitLogs: {},
    tasks: [],
    projects: [],
    goals: [],
    challenges: [],
    duels: [],
    invites: [],
    notifications: [],
    notificationsUnreadCount: 0,
    notificationsTotal: 0,
    notificationsPreviewLimit: 5,
    friendRequests: [],
    friendships: [],
    blockedUsers: [],
    leagueBoard: [],
    navigationBadges: {},
    monthlySocialProgress: {
        challengesParticipated: 0,
        challengesCreated: 0,
        bossJoined: false,
    },
    kaizenReview: {
        isOpen: false,
        periodLabel: 'Semana atual',
        lastViewedAt: null,
    },
    notificationPreferences: {
        enabled: true,
        streakRisk: true,
        missions: true,
        boss: true,
        levelUp: true,
        social: true,
        system: true,
        soundFeedback: true,
        kaizen: true,
    },
    subscriptionHistory: [],
    ownedEmporiumItemIds: [],
    xpHistory: [],
    dashboardOverview: null,
    socialCache: {},
}
